/**
 * MapCluster — several cameras drawn as one circle, on the geographic map.
 *
 * Zoomed out, a city's worth of pins piles into one unreadable smear. The map merges pins
 * that would overlap (see clusterPins) and draws each group as this circle instead:
 *
 *  · the DISC is the worst state among its cameras, in the same red / amber / blue / navy /
 *    slate the pins and the honeycomb use — a group with one critical camera is red;
 *  · the RING around it is the mix — how much of the group is critical, warning, info,
 *    online and offline, as arcs of the circle;
 *  · the number is how many cameras, and the badge how many active alerts;
 *  · alerting groups ripple at their severity's tempo, exactly like a pin.
 *
 * Clicking it zooms to its cameras (the page decides how). Constant screen size, like the
 * pins: `--map-k` counter-scales it while the map is mid-zoom.
 */
import type { HexColorState } from '../hexagon/types';
import { ALERT_FX } from '../hexagon/types';
// Side effect: MapMarker injects the ripple / pulse keyframes this circle reuses.
import './MapMarker';

export const STATE_COLOR: Record<HexColorState, string> = {
  red: '#e61b24',
  amber: '#f59e0b',
  blue: '#015ab5',
  grey: '#2c3e50',
  offline: '#94a3b8',
};

/** Worst first — the order the disc colour is chosen in and the ring is drawn in. */
export const STATE_ORDER: HexColorState[] = ['red', 'amber', 'blue', 'grey', 'offline'];

const STATE_WORD: Record<HexColorState, string> = {
  red: 'critical',
  amber: 'warning',
  blue: 'info',
  grey: 'online',
  offline: 'offline',
};

export interface MapClusterProps {
  /** Layer pixels — the group's centre. */
  left: number;
  top: number;
  count: number;
  /** How many members are in each state. */
  states: Partial<Record<HexColorState, number>>;
  /** Active alerts across the group. */
  alerts: number;
  /** Every member is excluded by the board filter. */
  dimmed?: boolean;
  onClick: () => void;
}

export function worstState(states: Partial<Record<HexColorState, number>>): HexColorState {
  return STATE_ORDER.find((s) => (states[s] ?? 0) > 0) ?? 'offline';
}

function ringGradient(states: Partial<Record<HexColorState, number>>): string {
  // Out of the cameras COUNTED, which can be fewer than the group when a filter dims some.
  const count = STATE_ORDER.reduce((t, s) => t + (states[s] ?? 0), 0) || 1;
  let at = 0;
  const stops: string[] = [];
  for (const s of STATE_ORDER) {
    const n = states[s] ?? 0;
    if (!n) continue;
    const end = at + (n / count) * 360;
    stops.push(`${STATE_COLOR[s]} ${at}deg ${end}deg`);
    at = end;
  }
  return `conic-gradient(${stops.join(', ')})`;
}

export default function MapCluster({ left, top, count, states, alerts, dimmed, onClick }: MapClusterProps) {
  const worst = worstState(states);
  // Grows with the group, but slowly: a 400-camera district must not cover the state.
  const size = Math.round(36 + Math.min(24, Math.log2(Math.max(2, count)) * 5));
  const fx = !dimmed ? ALERT_FX[worst] : undefined;
  const summary = STATE_ORDER.filter((s) => (states[s] ?? 0) > 0)
    .map((s) => `${states[s]} ${STATE_WORD[s]}`)
    .join(' · ');

  return (
    <button
      type="button"
      data-tour="map-cluster"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={`${count} cameras — ${summary}${alerts ? ` · ${alerts} active alert${alerts === 1 ? '' : 's'}` : ''}\nClick to zoom in`}
      aria-label={`${count} cameras, ${summary}. Zoom in.`}
      className="pointer-events-auto absolute cursor-pointer rounded-full border-0 bg-transparent p-0 transition-opacity hover:opacity-100 focus:outline-none"
      style={{
        left,
        top,
        width: size,
        height: size,
        transform: 'translate(-50%, -50%) scale(calc(1 / var(--map-k, 1)))',
        opacity: dimmed ? 0.35 : 1,
        // Alerting groups above calm ones — the same tiers MapMarker uses.
        zIndex: fx ? 20 : 10,
      }}
    >
      {fx && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{ background: fx.glow, animation: `mapPinRipple ${fx.duration} ease-out infinite` }}
        />
      )}
      {/* The state mix, as a ring. */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full shadow-md"
        style={{ background: ringGradient(states) }}
      />
      {/* White gap, then the disc in the worst state. */}
      <span aria-hidden className="absolute rounded-full bg-white" style={{ inset: 4 }} />
      <span
        className="absolute flex items-center justify-center rounded-full font-bold text-white"
        style={{
          inset: 6,
          background: STATE_COLOR[worst],
          fontSize: count > 99 ? 12 : 14,
          animation: fx ? `mapPinPulse ${fx.duration} ease-in-out infinite` : undefined,
        }}
      >
        {count}
      </span>
      {alerts > 0 && (
        <span
          className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white px-1 text-[10px] font-bold leading-none text-white"
          style={{ background: STATE_COLOR[worst === 'grey' || worst === 'offline' ? 'red' : worst] }}
        >
          {alerts > 99 ? '99+' : alerts}
        </span>
      )}
    </button>
  );
}
